import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
  when
} from 'mobx';
import * as Sentry from '@sentry/browser';

import UserModel from 'shared/models/UserModel';
import { FieldModel } from 'shared/models/form';
import {
  ManagerUiSettingsServer,
  normalizeManagerUiSettings,
  SOUND_NOTIFICATIONS_ENABLED_DEFAULT
} from 'shared/entities/managerUiSettings';
import { ValidatorResult } from 'shared/entities/validator';
import { apiUrls } from 'shared/entities/domain';
import { IRootStore } from 'shared/entities/store/rootStore';
import { IUserStore } from 'shared/entities/store/userStore';
import { LoadingStage } from 'shared/entities/meta';
import { LocalStorageKey } from 'shared/entities/localStorage';
import { localStorageHandler } from 'stores/localStorageHandler';
import {
  IUser,
  ManagerExternals,
  normalizeManagerExternals,
  normalizeUserTokens,
  UserAcceptInvitationServerType,
  UserAuthServer,
  UserTokens,
  mapRegistrationErrorToMessage,
  mapAuthErrorToMessage,
  LoginErrors,
  mapLoginErrorToMessage,
  RegistrationErrors,
  AuthErrors,
  IUserExtra,
  normalizeUserExtra,
  UserLoginServer,
  UserAuthInfoServer
} from 'shared/entities/user';
import { normalizeManagerSegmentVisibility } from 'shared/entities/managerSegment';
import { AccessRecoveryFastTrackServer } from 'shared/entities/accessRecovery';
import { AppNotificationType } from 'shared/entities/appNotifications';
import {
  AcceptInvitationErrorCode,
  mapAcceptInvitationErrorCodeToMessage
} from 'shared/entities/manager';
import { mapErrorCodeToMessage } from 'shared/entities/common/utils';
import { ChannelKind } from 'shared/entities/channels';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { AccountModel } from 'shared/models/AccountModel';
import ListModel from 'shared/models/ListModel/ListModel';
import { TimeoutError } from 'shared/models/errors';

export default class UserStore implements IUserStore {
  rootStore: IRootStore;

  private authInfoAbortController: AbortController | null = null;

  private segmentHandledVersion = 0;

  /**
   * Необходимость сегментации приходит из AUTH_INFO, поэтому `null` до первого
   * успешного ответа: у register и login этого поля в payload нет. После
   * успешного skip/complete frontend фиксирует `false` до следующего AUTH_INFO.
   */
  showSegment: boolean | null = null;

  user: UserModel | null;
  userTokens: UserTokens | null = null;
  userExtra: IUserExtra | null = null;
  userExternals: ManagerExternals | null;

  authStage: LoadingStage = LoadingStage.NOT_STARTED;
  registerStage: LoadingStage = LoadingStage.NOT_STARTED;
  loginStage: LoadingStage = LoadingStage.NOT_STARTED;
  logoutStage: LoadingStage = LoadingStage.NOT_STARTED;
  acceptStage: LoadingStage = LoadingStage.NOT_STARTED;

  shopbackAuthorizeStage: LoadingStageModel = new LoadingStageModel();

  /** Настройка менеджера, общая для всех проектов кабинета (US-18.2) */
  readonly soundNotificationsEnabled: FieldModel<boolean> =
    new FieldModel<boolean>(SOUND_NOTIFICATIONS_ENABLED_DEFAULT);

  readonly accounts: ListModel<AccountModel> = new ListModel<AccountModel>({
    entities: {},
    keys: []
  });

  constructor(rootStore: IRootStore) {
    makeObservable(this, {
      user: observable,
      authStage: observable,
      registerStage: observable,
      loginStage: observable,
      logoutStage: observable,
      acceptStage: observable,
      showSegment: observable,
      userTokens: observable,
      userExtra: observable,
      userExternals: observable,
      accounts: observable.ref,

      isAuthorized: computed,
      showedAccounts: computed,

      authorize: action,
      register: action,
      login: action,
      logout: action.bound,
      resetAuth: action,
      acceptInvitation: action,
      applyChanges: action,
      initAccounts: action,
      markSegmentHandled: action,
      refreshTokens: action.bound
    });

    this.rootStore = rootStore;
  }

  get isAuthorized(): boolean {
    return !!this.user;
  }

  get showedAccounts(): AccountModel[] {
    return this.accounts.items.filter(
      (item) => item.manager.value || item.linkParams.value
    );
  }

  removeToken = (
    kind: ChannelKind.VK | ChannelKind.FB | ChannelKind.IG
  ): void => {
    if (!this.userTokens) {
      return;
    }

    this.userTokens[kind] = false;

    if (!this.userExternals) {
      return;
    }

    delete this.userExternals[kind];
  };

  resetAuth = (): void => {
    this.cancelAuthInfoRequest();
    this.user = null;
    this.showSegment = null;
  };

  cancelAuthInfoRequest = (): void => {
    this.authInfoAbortController?.abort();
    this.authInfoAbortController = null;
  };

  markSegmentHandled = (): void => {
    this.segmentHandledVersion += 1;
    this.showSegment = false;
  };

  /**
   * Настройка per-manager, поэтому грузится один раз за сессию, а не на каждый проект.
   * Звать только после инициализации кабинета: createData подмешивает cabinet_id
   * в любой запрос, и до его выбора уйдёт пустая строка — бэк ответит
   * bad_request на невалидный ObjectId.
   */
  loadUiSettings = async (): Promise<void> => {
    const response =
      await this.rootStore.networkStore.api<ManagerUiSettingsServer>(
        apiUrls.MANAGERS_UI_SETTINGS_GET
      );

    if (response.isError) {
      return;
    }

    const { soundNotificationsEnabled } = normalizeManagerUiSettings(
      response.data
    );

    this.soundNotificationsEnabled.changeValue(soundNotificationsEnabled);
    this.soundNotificationsEnabled.resetTouched();
  };

  setSoundNotificationsEnabled = async (value: boolean): Promise<void> => {
    if (this.soundNotificationsEnabled.value === value) {
      return;
    }

    this.soundNotificationsEnabled.changeValue(value);

    const response = await this.rootStore.networkStore.api(
      apiUrls.MANAGERS_UI_SETTINGS_SET,
      {
        method: 'POST',
        data: { ui_settings: { sound_notifications_enabled: value } }
      }
    );

    if (response.isError) {
      this.soundNotificationsEnabled.reset();
      this.rootStore.appNotificationsStore.open({
        type: AppNotificationType.error,
        title: (t) =>
          t('UserStore.errorSaveSoundNotifications', { ns: 'stores' })
      });

      return;
    }

    this.soundNotificationsEnabled.resetTouched();
    this.rootStore.appNotificationsStore.open({
      type: AppNotificationType.success,
      title: (t) =>
        t('UserStore.successSaveSoundNotifications', { ns: 'stores' })
    });
  };

  /**
   * Тихо перечитывает только флаги токенов. В отличие от loadAuthInfo не отменяет чужие
   * запросы auth/info и не трогает профиль и showSegment: ошибка фоновой проверки
   * не должна сбрасывать опрос сегмента или показывать уведомления.
   */
  async refreshTokens(): Promise<BaseResponse> {
    const managerId = this.user?.id;

    if (!managerId) {
      return { isError: true };
    }

    const response = await this.rootStore.networkStore.api<UserAuthInfoServer>(
      apiUrls.AUTH_INFO,
      {
        showExpectedError: false,
        showUnexpectedError: false,
        showAuthModal: false
      }
    );

    // Пока шёл запрос, менеджер мог выйти или войти под другим аккаунтом: чужие токены не пишем
    if (
      response.isError ||
      this.user?.id !== managerId ||
      response.data.manager._id !== managerId
    ) {
      return { isError: true };
    }

    runInAction(() => {
      this.userTokens = normalizeUserTokens(response.data.tokens);
    });

    return { isError: false };
  }

  applyChanges = (raw: UserAuthServer): UserModel => {
    this.user = UserModel.fromJson(raw.manager);
    this.userTokens = normalizeUserTokens(raw.tokens);
    this.userExtra = normalizeUserExtra(raw.manager.extra);
    this.userExternals = normalizeManagerExternals(raw.manager.external_ids);

    return this.user;
  };

  async register(data: {
    email: string;
    password: string;
    name: string;
    captcha_token: string;
  }): Promise<BaseResponse<IUser, RegistrationErrors | null>> {
    if (this.registerStage === LoadingStage.LOADING) {
      return {
        isError: true,
        data: null
      };
    }

    this.registerStage = LoadingStage.LOADING;

    const response = await this.rootStore.networkStore.api<
      UserAuthServer,
      RegistrationErrors
    >(apiUrls.AUTH_REGISTER, {
      method: 'POST',
      data,
      errorsMap: mapRegistrationErrorToMessage,
      showExpectedError: false
    });

    if (!response.isError) {
      const user = this.applyChanges(response.data);
      await this.updateRefs();

      runInAction(() => {
        this.registerStage = LoadingStage.SUCCESS;
      });

      return {
        isError: false,
        data: user
      };
    } else {
      runInAction(() => {
        this.registerStage = LoadingStage.ERROR;
      });

      return {
        isError: true,
        data: response.data?.code || null
      };
    }
  }

  async loadAuthInfo<P = Record<string, any>>(): Promise<
    BaseResponse<UserAuthInfoServer<P>, ValidatorResult>
  > {
    // Актуален только последний запрос, поэтому предыдущий отменяем
    this.cancelAuthInfoRequest();
    const segmentHandledVersion = this.segmentHandledVersion;
    const abortController = new AbortController();
    this.authInfoAbortController = abortController;

    const response = await this.rootStore.networkStore.api<
      UserAuthInfoServer<P>,
      AuthErrors
    >(apiUrls.AUTH_INFO, {
      errorsMap: mapAuthErrorToMessage,
      showExpectedError: false,
      config: { signal: abortController.signal }
    });

    if (
      this.authInfoAbortController !== abortController ||
      abortController.signal.aborted
    ) {
      return {
        isError: true,
        data: null
      };
    }

    if (!response.isError) {
      this.applyChanges(response.data);
      if (segmentHandledVersion === this.segmentHandledVersion) {
        this.showSegment = normalizeManagerSegmentVisibility(
          response.data.manager
        ).showSegment;
      }

      // @ts-ignore TODO разобраться почему never
      return {
        isError: false,
        data: response.data
      };
    }

    // Отменённый ответ не трогает состояние: его уже заместил более свежий запрос
    if (
      !response.isCancelled &&
      segmentHandledVersion === this.segmentHandledVersion
    ) {
      this.showSegment = null;
    }

    return {
      isError: true,
      data: response.data?.code || null
    };
  }

  async authorize<P = Record<string, any>>(): Promise<
    BaseResponse<UserAuthInfoServer<P>, ValidatorResult>
  > {
    if (this.authStage === LoadingStage.LOADING) {
      return {
        isError: true,
        data: null
      };
    }

    this.authStage = LoadingStage.LOADING;

    const response = await this.loadAuthInfo<P>();

    if (!response.isError) {
      runInAction(() => {
        this.authStage = LoadingStage.SUCCESS;
      });
    } else {
      runInAction(() => {
        this.authStage = LoadingStage.ERROR;
      });
    }

    return response;
  }

  async acceptInvitation(
    data: UserAcceptInvitationServerType
  ): Promise<BaseResponse> {
    if (this.acceptStage === LoadingStage.LOADING) {
      return { isError: true };
    }
    this.acceptStage = LoadingStage.LOADING;

    const response = await this.rootStore.networkStore.api<
      UserAcceptInvitationServerType,
      AcceptInvitationErrorCode
    >(apiUrls.AUTH_ACCEPT_INVITATION, {
      method: 'POST',
      data: {
        ...data
      },
      errorsMap: mapAcceptInvitationErrorCodeToMessage,
      showUnexpectedError: false,
      showExpectedError: false
    });

    if (!response.isError) {
      runInAction(() => {
        this.acceptStage = LoadingStage.SUCCESS;
      });
      await this.authorize();
    } else {
      runInAction(() => {
        this.acceptStage = LoadingStage.ERROR;
      });

      if (response.data?.code !== AcceptInvitationErrorCode.managerNotFound) {
        this.rootStore.appNotificationsStore.open({
          type: AppNotificationType.error,
          title: mapErrorCodeToMessage({
            errorCode: response.data?.code || null,
            map: mapAcceptInvitationErrorCodeToMessage,
            defaultMessage: (t) =>
              t('manager.acceptInvitationErrors.unknown', {
                ns: 'entities'
              })
          })
        });
      }
    }

    return {
      isError: response.isError
    };
  }

  async login({ email, password, captcha_token }): Promise<
    BaseResponse<
      {
        accessRecoveryToken: string | null;
      },
      LoginErrors | null
    >
  > {
    this.loginStage = LoadingStage.LOADING;

    const response = await this.rootStore.networkStore.api<
      UserLoginServer,
      LoginErrors,
      AccessRecoveryFastTrackServer
    >(apiUrls.AUTH_LOGIN, {
      method: 'POST',
      data: {
        email,
        password,
        captcha_token
      },
      errorsMap: mapLoginErrorToMessage,
      showExpectedError: false
    });

    const accessRecoveryToken = !response.isError
      ? response.data.access_recovery?.recovery_token || null
      : response.data?.data?.access_recovery?.recovery_token || null;

    if (accessRecoveryToken) {
      this.loginStage = LoadingStage.SUCCESS;

      return {
        isError: false,
        data: {
          accessRecoveryToken
        }
      };
    }

    if (response.isError) {
      runInAction(() => {
        this.loginStage = LoadingStage.ERROR;
      });

      return {
        isError: true,
        data: response.data?.code || null
      };
    }

    this.applyChanges(response.data as UserAuthServer);

    runInAction(() => {
      this.loginStage = LoadingStage.SUCCESS;
      this.rootStore.uiStore.closeAuthorizedModal();
    });

    return {
      isError: false,
      data: {
        accessRecoveryToken: null
      }
    };
  }

  async logout(): Promise<BaseResponse> {
    if (!this.isAuthorized || this.logoutStage === LoadingStage.LOADING) {
      return {
        isError: true
      };
    }

    this.logoutStage = LoadingStage.LOADING;

    const response = await this.rootStore.networkStore.api(
      apiUrls.AUTH_LOGOUT,
      { method: 'POST' }
    );

    runInAction(() => {
      if (!response.isError) {
        this.cancelAuthInfoRequest();
        this.user = null;
        this.showSegment = null;
        this.logoutStage = LoadingStage.SUCCESS;
        localStorageHandler.remove(LocalStorageKey.authConsents);
      } else {
        this.logoutStage = LoadingStage.ERROR;
      }
    });

    return {
      isError: response.isError
    };
  }

  updateRefs = async (): Promise<BaseResponse> => {
    try {
      await when(() => false, { timeout: 4000 });
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setExtras(error.message);
        Sentry.captureException(
          new TimeoutError('Yandex Analytics was not initialized in 4000 ms')
        );
      });
    }

    const ymIdResponse = await this.rootStore.analyticsStore.ym.getId();

    return this.rootStore.networkStore.api(apiUrls.AUTH_UPDATE_REFS, {
      method: 'POST',
      data: {
        refs: {
          ...this.rootStore.routerStore.utmParams,
          open: this.rootStore.routerStore.openingParams.open,
          ym_id: !ymIdResponse.isError ? ymIdResponse.data : undefined,
          roistat_visit:
            this.rootStore.analyticsStore.roistat.id.value || undefined
        }
      }
    });
  };

  initAccounts = async (): Promise<void> => {
    const accountModels = await Promise.all(
      this.rootStore.pluginsStore.openedChannelsOrder.map((kind) => {
        return AccountModel.init({
          kind,
          manager: this.userExternals?.[kind] || null,
          rootStore: this.rootStore
        });
      })
    );
    const { keys, entities } = accountModels.reduce(
      (acc, model) => {
        if (!model) {
          return acc;
        }
        return {
          ...acc,
          entities: {
            ...acc.entities,
            [model.kind]: model
          },
          keys: [...acc.keys, model.kind]
        };
      },
      { entities: {}, keys: [] }
    );
    this.accounts.addEntities({ keys, entities, initial: true });
  };
}
